import React from 'react';
import { Helmet } from 'react-helmet-async';
interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  twitterCard?: string;
  robots?: string;
  hreflangAr?: string;
  hreflangEn?: string;
  hreflangDefault?: string;
  prevUrl?: string;
  nextUrl?: string;
  ogPrice?: number;
  productPrice?: number;
  productCurrency?: string;
  author?: string;
  publisher?: string;
  keywords?: string;
  jsonLd?: object | object[];
}
export function SEO({
  title,
  description,
  canonical,
  ogType = 'website',
  ogImage,
  ogPrice,
  productPrice,
  productCurrency = 'SAR',
  robots = 'index,follow',
  twitterCard = 'summary_large_image',
  hreflangAr,
  hreflangEn,
  hreflangDefault,
  prevUrl,
  nextUrl,
  author,
  keywords,
  jsonLd
}: SEOProps) {
  const fullTitle = `${title} | Dawaak`;
  const fullCanonical = canonical ? `https://www.innovaksa.net${canonical}` : undefined;
  return <Helmet data-id="element-5611" data-mp="3pm8xc13og4e0">
      {/* Resource Hints for Performance */}
      <link rel="preconnect" href="https://img.innovapharmacy.com" data-id="element-5612" data-mp="1td39dbxrmtuu" />
      <link rel="preconnect" href="https://img-test.innovaksa.net" data-id="element-5613" data-mp="1oetdez47vp86" />
      <link rel="preconnect" href="https://img.innovapharmacy.com" data-id="element-5614" data-mp="1oejozfytlq4l" />
      <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="anonymous" data-id="element-5615" data-mp="22558n6w3m43j" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" data-id="element-5616" data-mp="1y54rhehm7ess" />
      {/* Basic Meta Tags */}
      <title data-id="element-5617" data-mp="15g7cplcm404s">{fullTitle}</title>
      <meta name="description" content={description} data-id="element-5618" data-mp="15h90mqeq8h3x" />
      <meta name="author" content={author || 'Dawaak'} data-id="element-5619" data-mp="1jfp6yg8og5lj" />
      <meta name="publisher" content="Dawaak" data-id="element-5620" data-mp="1jh9zk0bflegr" />
      {keywords && <meta name="keywords" content={keywords} data-id="element-5621" data-mp="1eg46209y5n66" />}
      {fullCanonical && <link rel="canonical" href={fullCanonical} data-id="element-5622" data-mp="1eiypboh851y0" />}
      <meta name="robots" content={robots} data-id="element-5623" data-mp="2utu9d4t8s7er" />
      {/* Hreflang Tags */}
      {hreflangAr && <link rel="alternate" hreflang="ar-SA" href={`https://www.innovaksa.net${hreflangAr}`} data-id="element-5624" data-mp="2pur8lwkaoncl" />}
      {hreflangEn && <link rel="alternate" hreflang="en-SA" href={`https://www.innovaksa.net${hreflangEn}`} data-id="element-5625" data-mp="1sh7m3aoap2k5" />}
      {hreflangDefault && <link rel="alternate" hreflang="x-default" href={`https://www.innovaksa.net${hreflangDefault}`} data-id="element-5626" data-mp="1wgylg2rw0r0a" />}
      {/* Pagination Links */}
      {prevUrl && <link rel="prev" href={`https://www.innovaksa.net${prevUrl}`} data-id="element-5627" data-mp="14q23wouvty6o" />}
      {nextUrl && <link rel="next" href={`https://www.innovaksa.net${nextUrl}`} data-id="element-5628" data-mp="18r45hcc9umbt" />}
      {/* Open Graph Tags */}
      <meta property="og:type" content={ogType} data-id="element-5629" data-mp="1ij9y8oacl7s2" />
      <meta property="og:site_name" content="Dawaak" data-id="element-5630" data-mp="1ik1t1ww4f1ha" />
      <meta property="og:title" content={fullTitle} data-id="element-5631" data-mp="l45siond1win" />
      <meta property="og:description" content={description} data-id="element-5632" data-mp="l3ct68qqe9aj" />
      {fullCanonical && <meta property="og:url" content={fullCanonical} data-id="element-5633" data-mp="q1xn5qfe3wqj" />}
      {ogImage && <meta property="og:image" content={ogImage} data-id="element-5634" data-mp="ij7whg4cyqgk" />}
      <meta property="og:locale" content="en_US" data-id="element-5635" data-mp="iiolykh1f4ck" />
      <meta property="og:locale:alternate" content="ar_SA" data-id="element-5636" data-mp="djv98u9krhdg" />
      {ogPrice && <meta property="og:price:standard_amount" content={ogPrice.toString()} data-id="element-5637" data-mp="8la9evy8izpj" />}
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={twitterCard} data-id="element-5638" data-mp="4ix1q1zxgrq4" />
      <meta name="twitter:title" content={fullTitle} data-id="element-5639" data-mp="3vpq2j4qt6ou4" />
      <meta name="twitter:description" content={description} data-id="element-5640" data-mp="3vravwr1e01qe" />
      {ogImage && <meta name="twitter:image" content={ogImage} data-id="element-5641" data-mp="3qy89r3yxvuk8" />}
      {/* Product Price Tags */}
      {productPrice && <>
          <meta property="product:price:amount" content={productPrice.toString()} data-id="element-5642" data-mp="3m0g7bg0o4q1s" />
          <meta property="product:price:currency" content={productCurrency} data-id="element-5643" data-mp="3gz8zjgyizqv4" />
        </>}
      {/* JSON-LD Structured Data */}
      {jsonLd && <script type="application/ld+json" data-id="element-5644" data-mp="381gnclo3ozby">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd])}
        </script>}
    </Helmet>;
}